import { useAuth } from "@clerk/react"
import type { TSchema } from "@sinclair/typebox"
import { Value } from "@sinclair/typebox/value"
import type { IDBPDatabase } from "idb"
import { openDB } from "idb"
import { useCallback, useEffect, useRef, useState } from "react"
import type { StoredStateOptions } from "./useStoredState"
import { writeStoredStateValue } from "./useStoredState"

// ---------------------------------------------------------------------------
// DB singleton
// ---------------------------------------------------------------------------

const DB_NAME = "craft-force"
const STORE_NAME = "kv"
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase> | null = null

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
        }
      },
    })
  }
  return dbPromise
}

// ---------------------------------------------------------------------------
// Read / write helpers (same versioned envelope as useStoredState)
// ---------------------------------------------------------------------------

type StoredPayload<T> = { v: number; value: T }

/**
 * Strip remote sync fields from library records migrated from the shared
 * legacy key. When multiple users share the same device the legacy key may
 * hold records that were synced by a different user; keeping remoteSyncedAt
 * would cause mergeAuthoritativeRemoteRecords to drop them.
 */
function stripSyncFields<T>(value: T): T {
  if (!Array.isArray(value)) return value
  return value.map((item: unknown) => {
    if (!item || typeof item !== "object") return item
    const {
      remoteSyncedAt: _,
      remoteSyncConflictAt: __,
      remoteSyncFailedAt: ___,
      ...rest
    } = item as Record<string, unknown>
    return rest
  }) as T
}

async function idbRead<T>(
  key: string,
  schema: TSchema,
  options: StoredStateOptions<T>,
  legacyKey?: string,
): Promise<T> {
  try {
    const db = await getDb()
    const raw = await db.get(STORE_NAME, key)

    if (raw != null) {
      const parsed = raw as StoredPayload<unknown>
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.v === "number"
      ) {
        if (parsed.v !== options.version) {
          if (!options.migrate) return options.defaultValue
          return options.migrate(parsed.value, parsed.v, options.version)
        }

        const converted = Value.Convert(schema, parsed.value)
        if (Value.Check(schema, converted)) {
          return converted as T
        }
      }
    }

    // Nothing found under the scoped key — try migrating from legacy key
    if (legacyKey) {
      const legacyRaw = await db.get(STORE_NAME, legacyKey)
      if (legacyRaw != null) {
        const parsed = legacyRaw as StoredPayload<unknown>
        if (
          parsed &&
          typeof parsed === "object" &&
          typeof parsed.v === "number"
        ) {
          let migrated: T
          if (parsed.v !== options.version) {
            if (!options.migrate) return options.defaultValue
            migrated = options.migrate(parsed.value, parsed.v, options.version)
          } else {
            const converted = Value.Convert(schema, parsed.value)
            if (!Value.Check(schema, converted)) return options.defaultValue
            migrated = converted as T
          }
          // Strip sync metadata so records migrated from the shared legacy key
          // are treated as local on this user's scoped key. Without this, a
          // record that was synced by a different user (same unscoped key)
          // would be dropped by mergeAuthoritativeRemoteRecords when this
          // user's server has never seen it.
          const stripped = stripSyncFields(migrated)
          // Write to scoped key and remove legacy key
          const payload: StoredPayload<T> = {
            v: options.version,
            value: stripped,
          }
          await db.put(STORE_NAME, payload, key)
          await db.delete(STORE_NAME, legacyKey)
          return stripped
        }
      }
    }

    return options.defaultValue
  } catch {
    return options.defaultValue
  }
}

async function idbWrite<T>(
  key: string,
  value: T,
  version: number,
): Promise<void> {
  try {
    const db = await getDb()
    const payload: StoredPayload<T> = { v: version, value }
    await db.put(STORE_NAME, payload, key)
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Async-loaded, IndexedDB-backed state. Returns [value, isLoading, setValue].
 * Starts with defaultValue while loading; updates once the DB read resolves.
 *
 * Pass `key: null` to defer loading until the key is known (e.g. until userId
 * is available). While key is null, isLoading stays true.
 */
export function useIdbState<T>(
  key: string | null,
  schema: TSchema,
  options: StoredStateOptions<T>,
  legacyKey?: string,
): readonly [T, boolean, (next: T | ((previous: T) => T)) => void] {
  const [value, setValueRaw] = useState<T>(options.defaultValue)
  const [isLoading, setIsLoading] = useState(true)
  const isLoadedRef = useRef(false)
  const versionRef = useRef(options.version)
  const keyRef = useRef(key)

  useEffect(() => {
    keyRef.current = key
  }, [key])

  // Load from IDB when key becomes available (or changes)
  // biome-ignore lint/correctness/useExhaustiveDependencies: schema and options are stable at call site; key drives re-reads
  useEffect(() => {
    if (!key) {
      setIsLoading(true)
      isLoadedRef.current = false
      setValueRaw(options.defaultValue)
      return
    }

    let cancelled = false
    setIsLoading(true)
    isLoadedRef.current = false

    idbRead(key, schema, options, legacyKey).then((loaded) => {
      if (!cancelled) {
        setValueRaw(loaded)
        setIsLoading(false)
        isLoadedRef.current = true
      }
    })

    return () => {
      cancelled = true
    }
  }, [key])

  // Persist to IDB on every change after load completes
  useEffect(() => {
    if (!isLoadedRef.current || !keyRef.current) return
    idbWrite(keyRef.current, value, versionRef.current)
  }, [value])

  const setValue = useCallback((next: T | ((previous: T) => T)) => {
    setValueRaw((previous) =>
      typeof next === "function" ? (next as (prev: T) => T)(previous) : next,
    )
  }, [])

  return [value, isLoading, setValue] as const
}

// ---------------------------------------------------------------------------
// User-scoped variant
// ---------------------------------------------------------------------------

/**
 * Like useIdbState, but automatically scopes the key by the authenticated
 * userId so that data from different users on the same device never mixes.
 * Falls back to baseKey as a legacy migration source if the scoped key is
 * empty (one-time migration, then deletes the unscoped entry).
 */
export function useUserIdbState<T>(
  baseKey: string,
  schema: TSchema,
  options: StoredStateOptions<T>,
): readonly [T, boolean, (next: T | ((previous: T) => T)) => void] {
  const { userId } = useAuth()
  const scopedKey = userId ? `${baseKey}.${userId}` : null
  return useIdbState(scopedKey, schema, options, baseKey)
}

// ---------------------------------------------------------------------------
// Migration helper: copy one localStorage entry into IDB (one-time)
// ---------------------------------------------------------------------------

export async function migrateLocalStorageToIdb<T>(
  key: string,
  schema: TSchema,
  options: StoredStateOptions<T>,
): Promise<void> {
  if (
    typeof window === "undefined" ||
    typeof window.localStorage?.getItem !== "function"
  )
    return
  const raw = window.localStorage.getItem(key)
  if (!raw) return

  // Only migrate if IDB entry is absent
  try {
    const db = await getDb()
    const existing = await db.get(STORE_NAME, key)
    if (existing != null) {
      window.localStorage.removeItem(key)
      return
    }
  } catch {
    return
  }

  // Parse, validate, and write to IDB using the same logic as readStoredStateValue
  try {
    const parsed = JSON.parse(raw) as StoredPayload<unknown>
    if (!parsed || typeof parsed !== "object" || typeof parsed.v !== "number")
      return

    let migrated: T
    if (parsed.v !== options.version) {
      if (!options.migrate) return
      migrated = options.migrate(parsed.value, parsed.v, options.version)
    } else {
      const converted = Value.Convert(schema, parsed.value)
      if (!Value.Check(schema, converted)) return
      migrated = converted as T
    }

    await idbWrite(key, migrated, options.version)
    window.localStorage.removeItem(key)
  } catch {
    // leave localStorage intact if migration fails
  }
}

// Re-export for consumers that only need the write serializer
export { writeStoredStateValue }
